// ============================================================================
// Safe Care - Validation des demandes d'indemnisation (côté agent/validateur)
// Module : features/assistance_sociale/pages
// Route : /indemnisation/validation (ex onglet "Validation d'indemnisation"
//         de /assurance/validation)
// Charte : .tab / .formTitle / .bG / .clientP / .tabItem (identique à
//          /suspension/valide) — les actions sont sur la barre .bG.
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input, InputNumber } from 'antd';
import { FilePdfOutlined, EyeOutlined, CheckCircleOutlined, FileDoneOutlined } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import dayjs from 'dayjs';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import {
  useGetAllDemandesAssistance,
  useValiderDemandeAssistance,
  useRejeterDemandeAssistance,
  useGetJustificatifsByDemande,
  useGetContexteValidation,
} from '../hooks';
import { DemandeAssistance, JustificatifDemande } from '../types';

const ValidationDemandesIndemnisation = () => {
  const auth = useAuthUser();

  const getUserCode = () => {
    const u = auth();
    const raw = u?.coduti ?? (u as any)?.CodUti ?? (u as any)?.CODUTI ?? localStorage.getItem('codUti') ?? sessionStorage.getItem('codUti') ?? '';
    const cleaned = String(raw).trim();
    if (!cleaned || cleaned.toLowerCase() === 'undefined' || cleaned.toLowerCase() === 'null') return '';
    return cleaned;
  };

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  // ==================== ONGLET 1 : DEMANDES D'INDEMNISATION ====================
  const { data: demandesAssistance, isLoading: loadingDemandesAssistance, refetch } = useGetAllDemandesAssistance('EN_COURS');
  const [searchTermAssistance, setSearchTermAssistance] = useState('');
  const [selectedDemandeKeys, setSelectedDemandeKeys] = useState<React.Key[]>([]);
  const [modalValider, setModalValider] = useState(false);
  const [modalRejetAssist, setModalRejetAssist] = useState(false);
  const [montantIndemnisation, setMontantIndemnisation] = useState<number>(0);
  const [codopeIndem, setCodopeIndem] = useState('');
  const [motifRejetAssist, setMotifRejetAssist] = useState('');
  const valider = useValiderDemandeAssistance();
  const rejeter = useRejeterDemandeAssistance();

  const dataDemandes = demandesAssistance ?? [];
  const selectedDemandeAssist = dataDemandes.find((d) => d.IdDemandeAssistance === selectedDemandeKeys[0]);
  const { data: justifRes, isLoading: loadingJustifs } = useGetJustificatifsByDemande(selectedDemandeAssist?.IdDemandeAssistance);
  const justificatifs: JustificatifDemande[] = justifRes?.StatutCode === 200 ? justifRes.Result?.Table1 ?? [] : [];
  const peutTraiter = !!selectedDemandeAssist && selectedDemandeAssist.Statut === 'EN_COURS';

  // Contexte de validation chargé depuis la version Safe Care de la souscription
  const { data: contexte, isFetching: loadingContexte } = useGetContexteValidation(
    modalValider ? selectedDemandeAssist?.IdDemandeAssistance : undefined
  );
  const resteDisponible = contexte?.MontantPlafond != null
    ? Math.max(0, (contexte.MontantPlafond ?? 0) - (contexte.MontantDejaIndemnise ?? 0))
    : undefined;

  // Montant proposé selon le mode de calcul de la règle d'événement :
  //   FIXE                 -> Montant configuré
  //   POURCENTAGE          -> Pourcentage% × MontantDemande
  //   POURCENTAGE_PLAFONNE -> idem, borné à MontantPlafondCalcul
  // Borné au reste disponible ; le validateur peut le modifier avant de confirmer.
  const montantPropose = useMemo(() => {
    if (!contexte) return undefined;
    const demande = Number(contexte.MontantDemande ?? 0);
    let m: number;
    if (contexte.ModeCalcul === 'FIXE') {
      m = Number(contexte.MontantParDemande ?? demande);
    } else if (contexte.ModeCalcul === 'POURCENTAGE' || contexte.ModeCalcul === 'POURCENTAGE_PLAFONNE') {
      m = (demande * Number(contexte.Pourcentage ?? 0)) / 100;
      if (contexte.ModeCalcul === 'POURCENTAGE_PLAFONNE' && contexte.MontantPlafondCalcul != null) {
        m = Math.min(m, Number(contexte.MontantPlafondCalcul));
      }
    } else {
      m = demande;
    }
    if (resteDisponible != null) m = Math.min(m, resteDisponible);
    return Math.max(0, Math.round(m));
  }, [contexte, resteDisponible]);

  // Pré-remplit le code opération avec le CodOpeIndemnisation du produit
  // et le montant avec la proposition calculée (modifiable par le validateur)
  useEffect(() => {
    if (!contexte) return;
    if (contexte.CodOpeIndemnisation) setCodopeIndem(contexte.CodOpeIndemnisation);
    if (montantPropose != null) setMontantIndemnisation(montantPropose);
  }, [contexte, montantPropose]);

  const openValider = () => {
    if (!peutTraiter) return;
    const codeUtilisateur = getUserCode();
    if (!codeUtilisateur) {
      SweetAlert.fire({ title: 'Session invalide', text: 'Impossible de récupérer le code utilisateur.', icon: 'error' });
      return;
    }
    if ((selectedDemandeAssist?.UtiSai ?? '').trim().toUpperCase() === codeUtilisateur.toUpperCase()) {
      SweetAlert.fire({ title: 'Validation impossible', text: 'Vous ne pouvez pas valider une demande que vous avez vous-même enregistrée.', icon: 'warning' });
      return;
    }
    setMontantIndemnisation(selectedDemandeAssist?.MontantDemande ?? 0);
    setCodopeIndem('');
    setModalValider(true);
  };
  const confirmValider = () => {
    if (!selectedDemandeAssist) return;
    setLoading(true);
    valider.mutate(
      { idDemandeAssistance: selectedDemandeAssist.IdDemandeAssistance!, montantIndemnisation, codope: codopeIndem, utiVal: getUserCode() },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalValider(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            setSelectedDemandeKeys([]);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: () => setLoading(false),
      }
    );
  };
  const openRejeter = () => {
    if (!peutTraiter) return;
    setMotifRejetAssist('');
    setModalRejetAssist(true);
  };
  const confirmRejetAssist = () => {
    if (!selectedDemandeAssist) return;
    if (!motifRejetAssist.trim()) {
      SweetAlert.fire({ title: 'Motif de rejet obligatoire', icon: 'warning' });
      return;
    }
    setLoading(true);
    rejeter.mutate(
      { idDemandeAssistance: selectedDemandeAssist.IdDemandeAssistance!, motifRejet: motifRejetAssist, utiVal: getUserCode() },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalRejetAssist(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            setSelectedDemandeKeys([]);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: () => setLoading(false),
      }
    );
  };

  const openPieceJointe = (dataUri?: string) => {
    if (!dataUri) return;
    const [meta, b64] = dataUri.split(',');
    const mime = meta.match(/data:(.*?);/)?.[1] ?? 'application/octet-stream';
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    window.open(URL.createObjectURL(new Blob([bytes], { type: mime })), '_blank');
  };

  // Détermine si un justificatif est un PDF
  const isPdf = (j: JustificatifDemande) => (j.NomFichier ?? '').toLowerCase().endsWith('.pdf');

  // Modale de prévisualisation centrée
  const [previewJustif, setPreviewJustif] = useState<JustificatifDemande | null>(null);

  const filteredDemandes = useMemo(() => {
    if (!searchTermAssistance.trim()) return dataDemandes;
    const term = searchTermAssistance.toLowerCase();
    return dataDemandes.filter((d) =>
      [
        d.Numcli,
        d.NOMCLI,
        d.PRECLI,
        d.LibelleEvenement,
        d.NomProduit,
        d.Statut,
        d.MotifRejet,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [dataDemandes, searchTermAssistance]);

  const columnsDemandeAssistance: ColumnsType<DemandeAssistance> = [
    { title: 'N°', dataIndex: 'IdDemandeAssistance', key: 'IdDemandeAssistance', width: 60, align: 'center' },
    { title: 'Client', dataIndex: 'Numcli', key: 'Numcli' },
    { title: 'Nom', dataIndex: 'NOMCLI', key: 'NOMCLI' },
    { title: 'Prénom', dataIndex: 'PRECLI', key: 'PRECLI' },
    { title: 'Produit', dataIndex: 'NomProduit', key: 'NomProduit' },
    { title: 'Événement', dataIndex: 'LibelleEvenement', key: 'LibelleEvenement' },
    {
      title: 'Date sinistre',
      dataIndex: 'DateSinistre',
      key: 'DateSinistre',
      render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '',
    },
    { title: 'Montant demandé', dataIndex: 'MontantDemande', key: 'MontantDemande', align: 'right' },
    { title: 'Justif.', dataIndex: 'NbJustificatifs', key: 'NbJustificatifs', width: 70, align: 'center' },
    { title: 'Statut', dataIndex: 'Statut', key: 'Statut' },
    { title: 'Saisi par', dataIndex: 'UtiSai', key: 'UtiSai', width: 90 },
    { title: 'Motif rejet', dataIndex: 'MotifRejet', key: 'MotifRejet', ellipsis: true },
  ];

  // ==================== ONGLET 2 : INDEMNISATIONS VALIDÉES ====================
  const IndemnisationsValideesTab = () => {
    const { data: indemnisations, isLoading: loadingIndemnisations } = useGetAllDemandesAssistance('VALIDEE');
    const [searchTerm, setSearchTerm] = useState('');

    const filtered = useMemo(() => {
      const rows = indemnisations ?? [];
      if (!searchTerm.trim()) return rows;
      const term = searchTerm.toLowerCase();
      return rows.filter((d) =>
        [d.Numcli, d.NOMCLI, d.PRECLI, d.LibelleEvenement, d.NomProduit, d.UtiVal]
          .map((v) => String(v ?? '').toLowerCase())
          .some((v) => v.includes(term))
      );
    }, [indemnisations, searchTerm]);

    const columns: ColumnsType<DemandeAssistance> = [
      { title: 'N°', dataIndex: 'IdDemandeAssistance', key: 'IdDemandeAssistance', width: 60, align: 'center' },
      { title: 'Client', dataIndex: 'Numcli', key: 'Numcli' },
      { title: 'Nom', dataIndex: 'NOMCLI', key: 'NOMCLI' },
      { title: 'Prénom', dataIndex: 'PRECLI', key: 'PRECLI' },
      { title: 'Produit', dataIndex: 'NomProduit', key: 'NomProduit' },
      { title: 'Événement', dataIndex: 'LibelleEvenement', key: 'LibelleEvenement' },
      {
        title: 'Date sinistre',
        dataIndex: 'DateSinistre',
        key: 'DateSinistre',
        render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '',
      },
      { title: 'Montant demandé', dataIndex: 'MontantDemande', key: 'MontantDemande', align: 'right' },
      { title: 'Justif.', dataIndex: 'NbJustificatifs', key: 'NbJustificatifs', width: 70, align: 'center' },
      {
        title: 'Date validation',
        dataIndex: 'DatVal',
        key: 'DatVal',
        render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '',
      },
      { title: 'Validé par', dataIndex: 'UtiVal', key: 'UtiVal', width: 90 },
    ];

    return (
      <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
        <legend>Indemnisations validées ({filtered.length})</legend>
        <Input.Search
          placeholder="Rechercher une indemnisation..."
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: 8, width: 300 }}
        />
        <Spin spinning={loadingIndemnisations} size="large" style={{ color: '#116599' }}>
          <Table
            size="small"
            rowKey="IdDemandeAssistance"
            columns={columns}
            dataSource={filtered}
            scroll={{ x: 10 }}
            pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
          />
        </Spin>
      </Box>
    );
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span id="Tab"><CheckCircleOutlined /> Validation d'indemnisation</span>,
      children: null,
    },
    {
      key: '2',
      label: <span id="Tab"><FileDoneOutlined /> Indemnisations validées</span>,
      children: null,
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - VALIDATION DES INDEMNISATIONS</b>
      </Typography>
      <Spin spinning={loadingDemandesAssistance || loading} size="large" style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign="left" form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          {/* Barre d'action charte (.bG) : uniquement sur l'onglet de validation */}
          {activeTab === '1' && (
            <div className="bG">
              <legend>
                <Typography color="#116599">Actions</Typography>
              </legend>
              <Form.Item shouldUpdate className="bouttong">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!peutTraiter} onClick={openValider}>
                    Valider
                  </Button>
                  <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!peutTraiter} onClick={openRejeter}>
                    Rejeter
                  </Button>
                  <Button size="large" className="bt_add" type="ghost" htmlType="button" block onClick={() => refetch()}>
                    Actualiser
                  </Button>
                  <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={() => { setSelectedDemandeKeys([]); setSearchTermAssistance(''); }}>
                    Annuler
                  </Button>
                </Space>
              </Form.Item>
            </div>
          )}
          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
              />
            </div>
          </div>
        </Form>
        <div className="tabItem" style={{ display: 'block' }}>
          <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
            {activeTab === '1' ? (
              <>
                <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                  <legend>Demandes d'indemnisation en attente ({filteredDemandes.length})</legend>
                  <Input.Search
                    placeholder="Rechercher une demande..."
                    allowClear
                    value={searchTermAssistance}
                    onChange={(e) => setSearchTermAssistance(e.target.value)}
                    style={{ marginBottom: 8, width: 300 }}
                  />
                  <Table
                    size="small"
                    rowKey="IdDemandeAssistance"
                    rowSelection={{
                      type: 'radio',
                      selectedRowKeys: selectedDemandeKeys,
                      onChange: setSelectedDemandeKeys,
                      onSelect: (record, selected) => {
                        // Permet de désélectionner une ligne déjà sélectionnée
                        if (!selected) setSelectedDemandeKeys([]);
                      },
                    }}
                    onRow={(record) => ({
                      onClick: () => {
                        // Clic sur la ligne : bascule sélection/désélection
                        const id = (record as any).IdDemandeAssistance;
                        if (selectedDemandeKeys[0] === id) {
                          setSelectedDemandeKeys([]);
                        } else {
                          setSelectedDemandeKeys([id]);
                        }
                      },
                      style: { cursor: 'pointer' },
                    })}
                    columns={columnsDemandeAssistance}
                    dataSource={filteredDemandes}
                    scroll={{ x: 10 }}
                    pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
                  />
                </Box>

                {/* Panneau des justificatifs sous le tableau principal
                    (même positionnement que la page suspension) */}
                {selectedDemandeAssist ? (
                  <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                    <legend>
                      Justificatifs soumis — Demande #{selectedDemandeAssist.IdDemandeAssistance} ({selectedDemandeAssist.NOMCLI} {selectedDemandeAssist.PRECLI})
                    </legend>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Événement : <b>{selectedDemandeAssist.LibelleEvenement}</b> — Montant demandé : <b>{selectedDemandeAssist.MontantDemande}</b>
                      {selectedDemandeAssist.Description ? <> — {selectedDemandeAssist.Description}</> : null}
                    </Typography>
                    <Spin spinning={loadingJustifs} size="large" style={{ color: '#116599' }}>
                      {justificatifs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: 13 }}>
                          Aucun justificatif soumis pour cette demande
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                          {justificatifs.map((j, i) => {
                            const pdf = isPdf(j);
                            return (
                              <div
                                key={j.IdJustificatif ?? i}
                                onClick={() => setPreviewJustif(j)}
                                style={{
                                  width: 120, minHeight: 130, borderRadius: 10,
                                  border: '1px solid var(--colorOpaque030)',
                                  background: '#fff', cursor: 'pointer',
                                  display: 'flex', flexDirection: 'column',
                                  alignItems: 'center', justifyContent: 'center',
                                  position: 'relative', overflow: 'hidden',
                                  boxShadow: '0 2px 8px rgba(13,74,112, 0.08)',
                                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(13,74,112, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,74,112, 0.08)';
                                }}
                              >
                                {(() => {
                                  const label = j.LibelleDocument ?? j.Description ?? 'Document libre';
                                  return (
                                    <div style={{
                                      width: '100%', padding: '4px 6px',
                                      background: j.LibelleDocument ? 'var(--lightColor002)' : 'var(--colorOpaque020)',
                                      color: 'var(--darkColor)',
                                      fontSize: 10, fontWeight: 600, textAlign: 'center',
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                      borderBottom: '1px solid var(--colorOpaque030)',
                                    }} title={label}>
                                      {label}
                                    </div>
                                  );
                                })()}
                                {pdf ? (
                                  <>
                                    <FilePdfOutlined style={{ fontSize: 36, color: '#ff4d4f' }} />
                                    <div style={{ fontSize: 10, padding: '4px 6px', textAlign: 'center', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>
                                      {j.NomFichier ?? 'PDF'}
                                    </div>
                                  </>
                                ) : j.PieceJointe ? (
                                  <>
                                    <img
                                      src={j.PieceJointe}
                                      alt={j.NomFichier ?? `justif-${i}`}
                                      style={{ width: '100%', height: 78, objectFit: 'cover' }}
                                    />
                                    <div style={{ fontSize: 10, padding: '4px 6px', textAlign: 'center', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>
                                      {j.NomFichier ?? 'Image'}
                                    </div>
                                  </>
                                ) : (
                                  <div style={{ fontSize: 11, color: '#999' }}>Aperçu indisponible</div>
                                )}
                                <div style={{
                                  position: 'absolute', top: 4, right: 4,
                                  width: 22, height: 22, borderRadius: '50%',
                                  background: 'rgba(17,101,153, 0.85)', color: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 11,
                                }}>
                                  <EyeOutlined />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Spin>
                  </Box>
                ) : (
                  <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
                    <Box sx={{
                      py: 4, px: 2,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                    }}>
                      <Box sx={{
                        width: 56, height: 56, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'var(--lightColor002)',
                        color: 'var(--darkColor)',
                        fontSize: 28,
                      }}>
                        ☝
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: 'var(--darkblue)', fontSize: 15 }}>
                        Sélectionnez une demande d'indemnisation pour afficher ses justificatifs
                      </Typography>
                      <Typography sx={{ color: 'var(--colorOpaque060)', fontSize: 13 }}>
                        Choisissez une demande en cours dans le tableau ci-dessus pour visualiser les justificatifs soumis puis valider ou rejeter.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <IndemnisationsValideesTab />
            )}
          </div>
        </div>
      </Spin>

      {/* Modale de prévisualisation d'un justificatif — centrée, au-dessus de tout, border-radius 12px */}
      <Modal
        open={!!previewJustif}
        onCancel={() => setPreviewJustif(null)}
        footer={null}
        width="90%"
        centered
        zIndex={2100}
        className="safeCareModal"
        style={{ maxWidth: '1400px' }}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            {previewJustif?.NomFichier ?? 'Aperçu'}
          </div>
        }
      >
        {previewJustif && (
          <div style={{ textAlign: 'center' }}>
            {isPdf(previewJustif) && previewJustif.PieceJointe ? (
              <iframe
                src={previewJustif.PieceJointe}
                title={previewJustif.NomFichier ?? 'Aperçu PDF'}
                style={{ width: '100%', height: '75vh', border: '1px solid var(--colorOpaque030)', borderRadius: 10 }}
              />
            ) : previewJustif?.PieceJointe ? (
              <img
                src={previewJustif.PieceJointe}
                alt={previewJustif.NomFichier ?? 'Aperçu'}
                style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 10, border: '1px solid var(--colorOpaque030)' }}
              />
            ) : (
              <div style={{ padding: 40, color: '#999' }}>Aperçu indisponible</div>
            )}
            {previewJustif?.Description && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>{previewJustif.Description}</div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={`Valider la demande #${selectedDemandeAssist?.IdDemandeAssistance ?? ''}`}
        open={modalValider}
        onOk={confirmValider}
        onCancel={() => setModalValider(false)}
        okText="Valider"
        cancelText="Annuler"
        okButtonProps={{ loading }}
        className="safeCareModal"
      >
        <Spin spinning={loadingContexte}>
          <Form layout="vertical">
            {contexte && (
              <Box sx={{ p: 1.5, mb: 2, border: '1px solid #d9d9d9', borderRadius: 1, bgcolor: '#fafafa', fontSize: 13 }}>
                <div>Produit : <b>{contexte.NomProduit}</b> — Version {contexte.NumeroVersion}</div>
                <div>Événement : <b>{contexte.LibelleEvenement}</b></div>
                <div>
                  Montant par demande (config) : <b>{contexte.MontantParDemande != null ? Number(contexte.MontantParDemande).toLocaleString('fr-FR') : '—'}</b>
                  {contexte.ModeCalcul ? ` (${contexte.ModeCalcul}${contexte.Pourcentage != null ? ` ${contexte.Pourcentage}%` : ''})` : ''}
                </div>
                <div>
                  Plafond{contexte.TypePlafond ? ` (${contexte.TypePlafond})` : ''} : <b>{contexte.MontantPlafond != null ? Number(contexte.MontantPlafond).toLocaleString('fr-FR') : '—'}</b>
                </div>
                <div>
                  Demandes déjà effectuées : <b>{contexte.NbDemandesEffectuees ?? 0}</b>
                  {contexte.NbMaxUtilisations != null ? ` / ${contexte.NbMaxUtilisations} max${contexte.Periodicite ? ` (${contexte.Periodicite})` : ''}` : ''}
                </div>
                <div>Déjà indemnisé : <b>{Number(contexte.MontantDejaIndemnise ?? 0).toLocaleString('fr-FR')}</b></div>
                <div>Reste disponible : <b>{resteDisponible != null ? resteDisponible.toLocaleString('fr-FR') : '—'}</b></div>
                {montantPropose != null && (
                  <div style={{ color: 'var(--darkColor)', fontWeight: 600 }}>
                    Montant proposé (règle) : <b>{montantPropose.toLocaleString('fr-FR')}</b>
                    {contexte.ModeCalcul === 'POURCENTAGE' || contexte.ModeCalcul === 'POURCENTAGE_PLAFONNE'
                      ? ` = ${contexte.Pourcentage ?? 0}% × ${Number(contexte.MontantDemande ?? 0).toLocaleString('fr-FR')}`
                      : ''}
                  </div>
                )}
              </Box>
            )}
            <Form.Item label="Montant de l'indemnisation" required>
              <InputNumber style={{ width: '100%' }} min={0} max={resteDisponible} value={montantIndemnisation} onChange={(v) => setMontantIndemnisation(Number(v ?? 0))} />
            </Form.Item>
            <Form.Item label="Code opération (OPERAT)" tooltip="Défini une seule fois sur le produit Safe Care">
              <Input value={codopeIndem} readOnly placeholder="Code opération du produit" suffix={contexte?.lib_CodOpeIndemnisation ?? undefined} />
            </Form.Item>
            <Typography variant="body2" color="textSecondary">
              {justificatifs.length} justificatif(s) soumis.
            </Typography>
          </Form>
        </Spin>
      </Modal>

      <Modal
        title={`Rejeter la demande #${selectedDemandeAssist?.IdDemandeAssistance ?? ''}`}
        open={modalRejetAssist}
        onOk={confirmRejetAssist}
        onCancel={() => setModalRejetAssist(false)}
        okText="Rejeter"
        cancelText="Annuler"
        okButtonProps={{ danger: true, loading }}
        centered
        zIndex={1400}
        className="safeCareModal"
      >
        <Form layout="vertical">
          <Form.Item label="Motif du rejet" required>
            <Input.TextArea rows={3} value={motifRejetAssist} onChange={(e) => setMotifRejetAssist(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ValidationDemandesIndemnisation;
